import React from "react";
import {
  Box,
  Typography,
  Grid,
  Paper,
  Card,
  CardContent,
  Container,
} from "@mui/material";
import "./DataArchitecture.css"; // <-- Import CSS
import personaInsight from "../../../../assets/images/dataArchitecture/personaInsights.png";
import extensiveAudience from "../../../../assets/images/dataArchitecture/extensive-audiance.png";
import humanMind from "../../../../assets/images/dataArchitecture/human.png";
import backgroundImage from "../../../../assets/images/dataArchitecture/background.jpg";
import techImage from "../../../../assets/images/dataArchitecture/test.jpg";

const steps = [
  {
    number: "1",
    title: "Data Combination",
    desc: "Integrating all data types.",
  },
  {
    number: "2",
    title: "Intelligent Engine",
    desc: "Processing for deep insights.",
  },
  {
    number: "3",
    title: "Curated Deals",
    desc: "Personalized offers.",
  },
  {
    number: "4",
    title: "Campaign Activation",
    desc: "Effective campaign launches.",
  },
];
const DataArchitecture = () => {
  return (
    <Box className="da-root">
      {/* Hero Section */}
      <Box
        className="da-hero"
        sx={{
          backgroundImage: `url(${backgroundImage})`,
        }}
      >
        <Container maxWidth="lg">
          <Typography variant="h3" className="da-heading white hero-text">
            All Your Data. One Intelligent Engine.
          </Typography>
          <br />
          <Typography variant="h5" className="da-subheading white hero-text">
            Unlock true reach and insight with our AI-powered platform,
            transforming diverse data into curated deals and impactful
            campaigns.
          </Typography>
        </Container>
      </Box>

      {/* Data Sources */}
      <Box
        className="da-section da-source"
        // sx={{
        //   backgroundImage: `linear-gradient(#000000cc, #000000cc),url(${techImage})`,
        // }}
      >
        <Container maxWidth="lg">
          <Typography variant="h3" className="da-heading white">
            Data Source & Architecture
          </Typography>
          <br />
          <Typography variant="h5" className="da-subheading white">
            Our platform integrates diverse data types, from 1st Party data to
            2nd-party from programmatic platforms. We also incorporate 3rd-party
            data from data exchanges for comprehensive insights.
          </Typography>
        </Container>
      </Box>

      <Box
        sx={{
          backgroundColor: "#d3d3d3",
          py: 10,
          px: 2,
        }}
        className="da-section da-true-reach"
      >
        <Container maxWidth="lg">
          <Typography variant="h3" className="da-heading">
            True Reach, True Insight
          </Typography>
          <br />
          <Typography variant="h5" className="da-subheading">
            We combine and feed all data into our intelligent engines. This
            powerful synergy leads directly to curated deals and effective
            campaign activation.
          </Typography>
          <br />

          <br />
          <Grid container spacing={4}>
            {steps.map((step, index) => (
              <Grid item xs={12} md={6} key={index}>
                <Paper
                  elevation={2}
                  sx={{
                    backgroundColor: "#f5f5f5",
                    clipPath:
                      "polygon(0 0, calc(100% - 40px) 0, 100% 50%, calc(100% - 40px) 100%, 0 100%, 40px 50%)",
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    justifyContent: "center",
                    minHeight: 90,
                  }}
                >
                  <Typography variant="h4">{step.number}</Typography>
                </Paper>
                <br />
                <Typography variant="h5">{step.title}</Typography>
                <Typography variant="h5">{step.desc}</Typography>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* Customer Persona */}
      <Box className="da-section fade-in-up">
        <Container maxWidth="lg">
          <Grid container spacing={4} alignItems="center">
            <Grid item xs={12} md={7}>
              <Typography variant="h3" className="da-heading">
                Customer Persona Insights
              </Typography>
              <br />
              <Typography variant="h5" className="da-subheading">
                "Your Audience, Decoded." Our platform provides deep customer
                persona insights, enabling you to understand your audience on a
                human level. This leads to AI-ready data with human-level
                impact.
              </Typography>
            </Grid>
            <Grid item xs={12} md={5}>
              <Box
                component="img"
                src={personaInsight}
                alt="Customer Persona Insights"
                className="da-image"
                sx={{ width: "100%", borderRadius: 2 }}
              />
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* Audience Segments */}
      <Box className="da-section fade-in-up">
        <Container maxWidth="lg">
          <Typography variant="h3" className="da-heading">
            Extensive Audience Segments
          </Typography>
          <br />
          <Typography variant="h5" className="da-subheading">
            We offer over 135 audience segments, including "Fitness Freaks,"
            "Luxury Travellers," "Sports Fans," "Beauty Shoppers," and "Auto
            Enthusiasts." Other segments cover Finance, Food Lovers, Parenting,
            Fashion, Gaming, Tech Enthusiasts, and Professionals etc.
          </Typography>
          <Box
            component="img"
            src={extensiveAudience}
            className="da-image-wide"
          />
        </Container>
      </Box>
      <Box className="da-section fade-in-up">
        <Container maxWidth="lg">
          <Typography variant="h3" className="da-heading">
            Audience Composition
          </Typography>
          <br />

          <Typography variant="h5" className="da-subheading">
            Visualize your audience with our detailed composition graphs and
            data confidence funnel. Understand the makeup of your customer base
            and how they move with your brand over time.{" "}
          </Typography>
        </Container>
      </Box>

      {/* Data Quality */}
      <Box className="da-section fade-in-up">
        <Container maxWidth="lg">
          <Typography variant="h3" className="da-heading">
            Not All Data Is Created Equal
          </Typography>
          <br />
          <Typography variant="h5" className="da-subheading">
            We prioritize high-signal, high-quality data. This includes
            1st-party data, OS-level data, and context-enriched segments. This
            precision, not just volume, is what truly drives performance for
            your campaigns.
          </Typography>
          <br />
          <br />

          <Grid container spacing={3}>
            {[
              {
                title: "High-Signal Data",
                desc: "Focus on quality over quantity.",
              },
              { title: "OS-Level Data", desc: "Deep system integration." },
              {
                title: "1st-Party Data",
                desc: "Direct and reliable insights.",
              },
              { title: "Context-Enriched", desc: "Segments for precision." },
            ].map((item, idx) => (
              <Grid item xs={12} md={6} key={idx}>
                <Card
                  className="da-quality-card"
                  elevation={2}
                  sx={{ backgroundColor: "#d3d3d3", borderRadius:'8px', px:1, py:1 }} 
                >
                  <CardContent>
                    <Typography variant="h6" className="da-step-title">
                      {item.title}
                    </Typography>
                    <Typography variant="h6" className="da-step-desc">
                      {item.desc}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* AI Ready */}
      <Box className="da-section fade-in-up">
        <Container maxWidth="lg">
          <Grid container spacing={4} alignItems="center">
            {/* Left: Image */}
            <Grid item xs={12} md={5}>
              <Box
                component="img"
                src={humanMind}
                alt="AI Ready Data"
                className="da-image"
                sx={{ width: "100%", borderRadius: 2 }}
              />
            </Grid>

            {/* Right: Text */}
            <Grid item xs={12} md={7}>
              <Typography variant="h3" className="da-heading">
                AI-Ready Data, Human-Level Impact
              </Typography>
              <br />
              <Typography variant="h5" className="da-subheading">
                Our commitment to high-quality data ensures that every insight
                is actionable and impactful. Experience the difference that
                precise, intelligent data makes for your business.
              </Typography>
              <br />
              <br/>
               <Typography align="center" variant="h4" className="da-heading">
                BrandMo - Customer that moves with you
              </Typography>
              <br/>
            </Grid>
          </Grid>
        </Container>
      </Box>
    </Box>
  );
};

export default DataArchitecture;
